import { useContext } from 'react';
import { useHasFF } from 'venn-utils';
import {
  hasUnmatchedFunds,
  hasUnassignedInvestments,
  historicalPortfolioHasDuplicatedInvestments,
} from '../../../review/helpers';
import { MultiPortfolioReviewContext } from '../../common/MultiPortfolioReviewContext';

const NEEDS_MAPPING_TOOLTIP_MESSAGE =
  'Some portfolios contain investments that are not mapped to investments in the Data Library. Please ensure all investments are mapped in order to continue.';
const NEEDS_ASSIGNMENT_TOOLTIP_MESSAGE =
  'Some investments require strategy assignment. Please assign strategies to all mapped investments in order to continue.';
const HAS_DUPLICATE_ALLOCATIONS_TOOLTIP_MESSAGE =
  'Ensure that for any given strategy, only a single instance of an investment is included.';

export const useFooter = ({ isHistorical }: { isHistorical: boolean }) => {
  const hasNewPermissions = useHasFF('new_permissions_ff');
  const {
    actions: { onBackButtonClick, completeUpload, goToAssignOwnership },
    data: {
      existingParsedData,
      newParsedData,
      strategyData,
      savePortfoliosMutationState: { isIdle },
    },
  } = useContext(MultiPortfolioReviewContext);

  const includedParsedData = [...existingParsedData, ...newParsedData];

  const anyPortfolioHasUnmatchedFunds = includedParsedData.some((parsedResult) =>
    hasUnmatchedFunds(parsedResult.parsedPortfolio, parsedResult.excludedInvestments),
  );

  const anyPortfolioHasUnassignedInvestments = includedParsedData.some((parsedResult) =>
    hasUnassignedInvestments(parsedResult.parsedPortfolio, parsedResult.excludedInvestments, {
      hasManualStrategyAssignment: strategyData.hasManualStrategyAssignment,
    }),
  );

  const anyPortfolioHasDuplicateInvestments =
    isHistorical &&
    includedParsedData.some((parsedResult) =>
      historicalPortfolioHasDuplicatedInvestments(parsedResult.parsedPortfolio, parsedResult.excludedInvestments),
    );

  return {
    onCancel: onBackButtonClick,
    onContinue: hasNewPermissions && goToAssignOwnership ? goToAssignOwnership : completeUpload,
    primaryLabel: hasNewPermissions ? 'Continue' : 'Complete Upload',
    disabled:
      !isIdle ||
      anyPortfolioHasUnmatchedFunds ||
      anyPortfolioHasUnassignedInvestments ||
      anyPortfolioHasDuplicateInvestments,
    primaryTooltip: anyPortfolioHasUnmatchedFunds
      ? NEEDS_MAPPING_TOOLTIP_MESSAGE
      : anyPortfolioHasUnassignedInvestments
        ? NEEDS_ASSIGNMENT_TOOLTIP_MESSAGE
        : anyPortfolioHasDuplicateInvestments
          ? HAS_DUPLICATE_ALLOCATIONS_TOOLTIP_MESSAGE
          : undefined,
  };
};
